import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import type { TopSeller } from '../../../hooks/inventory/useInventoryDashboard';

interface TopSellersChartProps {
  data: TopSeller[];
  className?: string;
}

type Metric = 'revenue' | 'units';

export function TopSellersChart({ data, className = '' }: TopSellersChartProps) {
  const [metric, setMetric] = useState<Metric>('revenue');

  const chartData = data.map((s) => ({
    label: `${s.product_name}${s.size ? ` ${s.size}` : ''}${s.color ? `·${s.color}` : ''}`,
    revenue: Math.round(s.revenue_cents / 100),
    units: s.units_sold,
  }));

  if (chartData.length === 0) {
    return (
      <Card className={`bg-[#221F1F] border-[#504A4A] ${className}`}>
        <CardHeader>
          <CardTitle className="text-text-primary">Top Sellers</CardTitle>
        </CardHeader>
        <CardContent className="py-12 text-center text-text-secondary text-sm">
          No sales in the last 30 days yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`bg-[#221F1F] border-[#504A4A] ${className}`}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-text-primary">Top Sellers (30d)</CardTitle>
        <div className="flex gap-1 text-xs">
          <button
            type="button"
            onClick={() => setMetric('revenue')}
            className={`px-3 py-1 rounded-md transition ${
              metric === 'revenue'
                ? 'bg-[#9B1421] text-white'
                : 'bg-[#363232] text-white/70 hover:text-white'
            }`}
          >
            Revenue
          </button>
          <button
            type="button"
            onClick={() => setMetric('units')}
            className={`px-3 py-1 rounded-md transition ${
              metric === 'units'
                ? 'bg-[#9B1421] text-white'
                : 'bg-[#363232] text-white/70 hover:text-white'
            }`}
          >
            Units
          </button>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(169, 163, 163, 0.2)" />
            <XAxis
              dataKey="label"
              stroke="#A9A3A3"
              style={{ fontSize: '11px' }}
              angle={-30}
              textAnchor="end"
              height={70}
              interval={0}
            />
            <YAxis
              stroke="#A9A3A3"
              style={{ fontSize: '12px' }}
              tickFormatter={(v) => (metric === 'revenue' ? `$${v}` : `${v}`)}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#221F1F',
                border: '1px solid #504A4A',
                borderRadius: '8px',
                color: '#EBFFEE',
              }}
              labelStyle={{ color: '#A9A3A3' }}
              formatter={(value) => {
                const num = typeof value === 'number' ? value : 0;
                return metric === 'revenue'
                  ? [`$${num.toLocaleString()}`, 'Revenue']
                  : [num, 'Units'];
              }}
            />
            <Bar dataKey={metric} fill="#9B1421" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
